// Edit-in-place text field. Click to focus, blur or Cmd/Ctrl+Enter to save,
// Esc to cancel. Per Mockup Spec §5: "Single click to focus, blur or Enter
// to save, Esc to cancel."

import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

type Props = {
  value: string | null;
  placeholder?: string;
  multiline?: boolean;
  onSave: (next: string | null) => Promise<void> | void;
  className?: string;
  /** Optional label for the empty state */
  emptyLabel?: string;
};

export function EditInPlace({
  value,
  placeholder = 'Click to add',
  multiline = true,
  onSave,
  className = '',
  emptyLabel,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  // sync draft if value changes externally
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const len = (ref.current as HTMLTextAreaElement).value.length;
      try {
        (ref.current as HTMLTextAreaElement).setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    }
  }, [editing]);

  async function commit() {
    const next = draft.trim() === '' ? null : draft;
    if ((next ?? null) === (value ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value ?? '');
    setEditing(false);
    setError(null);
  }

  if (!editing) {
    const isEmpty = !value;
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={[
          'group flex w-full items-start gap-2 rounded-control border border-transparent p-2 text-left transition-colors hover:border-border hover:bg-bg',
          className,
        ].join(' ')}
      >
        <span
          className={[
            'flex-1 whitespace-pre-wrap text-[14px]',
            isEmpty ? 'italic text-muted' : 'text-ink',
          ].join(' ')}
        >
          {value ?? emptyLabel ?? placeholder}
        </span>
        <Pencil
          aria-hidden="true"
          size={13}
          className="shrink-0 translate-y-1 text-muted opacity-0 transition-opacity group-hover:opacity-100"
        />
      </button>
    );
  }

  return (
    <div className={['rounded-control border border-bartlett/30 bg-white p-2', className].join(' ')}>
      {multiline ? (
        <textarea
          ref={(el) => {
            ref.current = el;
          }}
          value={draft}
          rows={Math.min(8, Math.max(3, draft.split('\n').length + 1))}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void commit();
            }
          }}
          className="w-full resize-y bg-transparent text-[14px] outline-none"
          placeholder={placeholder}
        />
      ) : (
        <input
          ref={(el) => {
            ref.current = el;
          }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            }
          }}
          className="w-full bg-transparent text-[14px] outline-none"
          placeholder={placeholder}
        />
      )}
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted">
        <span>{saving ? 'saving…' : 'Esc to cancel · ⌘/Ctrl+Enter to save'}</span>
        {error && <span className="text-flag-critical">{error}</span>}
      </div>
    </div>
  );
}
